import { API_CONFIG } from "@/config/api";

/**
 * Extracts the filename from a full URL or returns the string if it's already a filename.
 * Handles localhost/127.0.0.1 URLs by extracting just the filename.
 */
export function extractFilenameFromUrl(urlOrFilename: string): string {
  // If it's a localhost or 127.0.0.1 URL, extract just the filename
  if (urlOrFilename.includes("127.0.0.1") || urlOrFilename.includes("localhost")) {
    const parts = urlOrFilename.split("/");
    const filename = parts[parts.length - 1];
    // Decode in case it was URL encoded
    return decodeURIComponent(filename);
  }

  return urlOrFilename;
}

/**
 * Normalizes an avatar URL or filename to a full URL using the current SUPABASE_URL.
 * Handles:
 * - null/undefined -> returns placeholder
 * - localhost/127.0.0.1 URLs -> extracts filename and reconstructs with current SUPABASE_URL
 * - Full URLs (non-localhost) -> returns as-is
 * - Filenames -> constructs full URL
 *
 * @param urlOrFilename - The avatar URL or filename
 * @param bucket - The storage bucket to use ('temporary-student-avatars' or 'avatars')
 */
export function normalizeAvatarUrl(
  urlOrFilename: string | null | undefined,
  bucket: "temporary-student-avatars" | "avatars" = "temporary-student-avatars"
): string {
  if (!urlOrFilename) {
    // Return default avatar from temporary-student-avatars bucket
    const defaultFileName = "avatar-default.png";
    const baseUrl = API_CONFIG.TEMPORARY_STUDENT_AVATARS_STORAGE_URL;
    return `${baseUrl}/${defaultFileName}`;
  }

  // Extract filename if it's a localhost URL
  const cleanedValue = extractFilenameFromUrl(urlOrFilename);

  // If it's already a full URL (and not localhost), return as-is
  if (
    (cleanedValue.startsWith("http://") || cleanedValue.startsWith("https://")) &&
    !cleanedValue.includes("127.0.0.1") &&
    !cleanedValue.includes("localhost")
  ) {
    return cleanedValue;
  }

  // Otherwise, it's a filename - construct the URL using the appropriate bucket
  const encodedFileName = encodeURIComponent(cleanedValue);
  const baseUrl =
    bucket === "avatars"
      ? `${API_CONFIG.SUPABASE_URL}/storage/v1/object/public/avatars`
      : API_CONFIG.TEMPORARY_STUDENT_AVATARS_STORAGE_URL;

  return `${baseUrl}/${encodedFileName}`;
}

/**
 * Determines the appropriate bucket for an avatar URL based on its path.
 */
export function getBucketFromUrl(url: string): "temporary-student-avatars" | "avatars" {
  if (url.includes("/avatars/") && !url.includes("temporary-student-avatars")) {
    return "avatars";
  }
  return "temporary-student-avatars";
}

/**
 * Determines the appropriate bucket for a filename.
 * If the filename looks like a UUID (which generated/upgraded avatars use), returns "avatars".
 * Otherwise returns "temporary-student-avatars".
 */
export function getBucketForFilename(filename: string): "temporary-student-avatars" | "avatars" {
  // If filename is empty or undefined, default to temp
  if (!filename) return "temporary-student-avatars";

  // Check if it's an initial avatar (usually simple names like 'boy1.png' etc.)
  // OR if it's explicitly from temporary bucket (some legacy data might have full URL)
  if (filename.includes("temporary-student-avatars")) {
    return "temporary-student-avatars";
  }

  // UUID pattern - used for generated/upgraded avatars
  // BUT: some initial avatars might also be UUIDs (e.g. uploaded by user)
  // To be safe: if it's a UUID, we check if it looks like a generated one.
  // Actually, standard UUID regex is reliable enough for generated ones.
  // The key issue the user reported is that they swapped to an INITIAL avatar, but it was loading from AVATARS.
  // This means the initial avatar filename (e.g. 'boy1.png') was being treated as a UUID?
  // 'boy1.png' does NOT match UUID regex.
  // So why did it fail?

  // Maybe the regex is too loose?
  // /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
  // This expects the string to START with UUID.
  // If the filename is just "boy1.png", it fails regex -> returns "temporary-student-avatars".
  // This is correct.

  // However, the user says: "images are AGAIN BROKEN EVERYWHERE, BECAUSE NOW YOU ARE LOADING FROM AVATARS INSTEAD OF TEMPORARY STUDENT AVATARS"
  // This implies that for initial avatars, my logic returns "avatars".
  // This implies `isUUID` is true for them?

  // Wait! If the user selected an initial avatar, `profile.avatar_url` should be NULL.
  // But maybe `profile.avatar_url` was NOT cleared?
  // Let's check `useTemporaryStudentAvatar.tsx`.

  // If `profile.avatar_url` is still set to the OLD upgraded avatar, but the user wants to show the NEWLY selected initial avatar...
  // Ah! `saveSelectedAvatar` updates `student_avatar_image_temporary`.
  // Does it clear `avatar_url`?

  // I need to check `src/hooks/useTemporaryStudentAvatar.tsx`.

  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(filename);
  if (isUUID) {
    return "avatars";
  }
  return "temporary-student-avatars";
}
